import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DatasetActions } from 'Redux/thunks/datasets.thunks';

import { useDatasetsListColumns } from '../hooks/useDatasetsListColumns.hook';
import { useLoadInternalDatasets } from '../hooks/useLoadInternalDatasets.hook';

import {
  defaultSortBy,
  defaultVisibleCols,
  defaultVisibleFacets,
  filterLabelMapping,
  emptyFilterDatasets,
  getSystemsDefaultFilter,
} from '../constants/datasetsListTableDefaults';
import { getDatasetsWithGlobal } from '../utils/datasets.utils';

import { DatahubSettings } from '../containers/DatahubSettings.container';

import FilterTable from '../components/filter-table/FilterTable';
import { LoadingPanel } from '../components/common/LoadingPanel';
import { ListActions } from '../components/common/ListActions.view';

import type { RootState } from '../types/state.types';

export const InternalDatasets = () => {
  const dispatch = useDispatch();

  const internalDatasets = useSelector((state: RootState) =>
    getDatasetsWithGlobal(state.internalEntities.datasets, state.pipes, state.upstreams)
  );
  const pipes = useSelector((state: RootState) => state.pipes);
  const upstreams = useSelector((state: RootState) => state.upstreams);
  const datasetsLoaded = useSelector((state: RootState) => state.loadStatus.datasetsLoaded);
  const token = useSelector((state: RootState) => state.subscription.token);

  const columns = useDatasetsListColumns(pipes, upstreams, 'internal-datasets');

  const { loadInternalDatasets } = useLoadInternalDatasets(token);

  const renderActions = (selectedIds: string[], clearSelection: () => void) => {
    const selectedDatasets = getSelectedDatasets(selectedIds);

    const deleteDatasets = () => {
      onDelete(selectedIds);
      clearSelection();
    };

    return <ListActions selection={selectedDatasets} onDelete={deleteDatasets} name="dataset" />;
  };

  const deleteDataset = (id: string) => dispatch(DatasetActions.delete(id));

  const onDelete = (selectedIds: string[]) =>
    selectedIds.forEach((datasetId) => deleteDataset(datasetId));

  const getSelectedDatasets = (selectedIds: string[]) =>
    internalDatasets.filter((dataset) => selectedIds.includes(dataset._id));

  const handleFilterChanged = useCallback(() => {
    loadInternalDatasets();
  }, [loadInternalDatasets]);

  if (datasetsLoaded === false) {
    return <LoadingPanel loadingMessage="Loading internal datasets..." />;
  }

  return (
    // @ts-ignore
    <DatahubSettings includeInternal={true} shouldRenderRefreshButton="render">
      <FilterTable
        actionsRenderer={renderActions}
        defaults={{
          filter: getSystemsDefaultFilter('system'),
          sortBy: defaultSortBy,
          visibleCols: defaultVisibleCols,
          visibleFacets: defaultVisibleFacets,
        }}
        emptyFilter={emptyFilterDatasets}
        filterLabelMapping={filterLabelMapping}
        columns={columns}
        itemsToFilter={internalDatasets}
        tableKey="internalDatasets"
        onFilterChanged={handleFilterChanged}
      />
    </DatahubSettings>
  );
};
